import { z } from 'zod';
import { EMPLOYEE_COUNT_MAX } from '../limits.js';
import { IDSchema, IsoTimestampSchema } from './shared.js';
/** QA 2026-10-01 F-08 — teams on one intake form (matches the quote line). */
const INTAKE_TEAMS_MAX = 1000;
/**
 * T6-001-AMEND — Intake form schema (Phase 6; locked product decisions).
 *
 * The customer-facing intake form completes the proposal → engagement
 * handoff. Customer arrives at `intake.therightrole.com/intake/<token>`
 * via the `intake_send` email after their proposal is signed; fills out
 * the form; submit advances Deal `signed → intakeSubmitted` and fires
 * `intake_received` notification to the tech team (T6-010).
 *
 * # Locked product decisions (T6-001-AMEND, 2026-05-29)
 *
 * Five questions surfaced during Kenny's #450 walk; the schema below
 * implements the decisions. The original T6-001 shape was incomplete for
 * production use — it worked only for the Team Assessments product flow,
 * lacked validation rigor (no individuals.length/individualCount match,
 * no consent capture, no contact splitting), and missed billing/comms
 * fields the tech team needs at handoff.
 *
 *   Q-A — `companyName`: editable on the form, defaults from CRM Account.
 *         No schema change (already a free string); form UI handles the
 *         default + edit affordance.
 *   Q-B — Contact structure: SPLIT WITH OPTIONAL SAME-AS. The form-filler
 *         is `primaryContact` (required). The day-to-day engagement lead is
 *         `engagementLead` (same shape, required by refinement when
 *         `engagementLeadSameAsPrimary === false`). Default UI checkbox is
 *         "Engagement lead is the same as primary contact" (checked).
 *   Q-C — `individualCount` vs `individuals.length`: MATCH REQUIRED with
 *         TBD support. Refinement asserts `individuals.length ===
 *         individualCount` at submit time. An individual entry whose
 *         `name === 'TBD'` may have `email === null` — signals the tech
 *         team to follow up for that slot. All non-TBD names require an
 *         email. Prevents accidental partial intake.
 *   Q-D — Product-specific intake variants: SHIP AS-IS, file STUB. The
 *         current `teams[]` shape covers Team Assessments. Internal Hire,
 *         External Hire, and Succession Planning need different form
 *         shapes (single position + candidates[], hierarchical level1/2,
 *         etc.) — those land post-Phase-6 as a discriminated union over
 *         product type. See `tickets/T-INTAKE-PRODUCT-SPECIFIC-FORMS.md`.
 *         No schema change required here — the union extension is
 *         non-breaking for Team-Assessments consumers.
 *   Q-E — Five new required/optional fields:
 *         - `dataHandlingConsent: z.literal(true)` — required customer
 *           acknowledgment that they're authorized to share the employee
 *           data with Right Role.
 *         - `preferredCommunicationChannel: z.enum(['email', 'teams',
 *           'slack', 'phone'])` — required, no default. The pre-fill
 *           payload (T6-003) MAY suggest a value if known on the CRM
 *           Account; the customer can change it.
 *         - `billingContact` + `billingContactSameAsPrimary` — mirrors
 *           the engagementLead split (Q-B). Required by refinement when
 *           `billingContactSameAsPrimary === false`.
 *         - `poNumber: z.string().max(80).optional()` — optional invoice
 *           reference / PO number captured at intake to thread through
 *           QuickBooks invoice generation in Phase 7.
 *
 * # Forward compatibility (V2+)
 *
 * Non-breaking extension is the migration path: additional sections, file
 * uploads, compliance acks, product-specific variants. Do NOT bump a schema
 * version field — that lives on the persisted `intake_drafts` / Document
 * records, not on this transient form contract. T6-007 (submit endpoint)
 * is the first downstream consumer; it MUST build against this amended
 * shape (refinements + new fields), not the original T6-001.
 */
/**
 * Single intake individual. The form's per-team grid renders one row per
 * individual; the customer may leave a row's `name` as the literal `'TBD'`
 * if they don't yet know who the assessment slot belongs to (Q-C). TBD
 * entries are allowed to have `email === null`; non-TBD entries MUST have
 * a valid email (enforced by `IndividualHasEmailUnlessTbdSchema`).
 *
 * `role` stays optional — the tech team can chase that down at kickoff if
 * the customer doesn't pre-populate it.
 */
export const IntakeIndividualBaseSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().nullable(),
    role: z.string().max(120).optional(),
});
export const IntakeIndividualSchema = IntakeIndividualBaseSchema.superRefine((val, ctx) => {
    // Q-C — TBD individuals may have null email; non-TBD individuals MUST
    // have a real email. The form UI hides the email field when the user
    // chooses "TBD" from a dropdown and re-shows it otherwise.
    if (val.name !== 'TBD' && (val.email === null || val.email === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'email is required unless name is "TBD"',
        });
    }
});
/**
 * Reusable contact shape — used for `primaryContact`, `engagementLead`,
 * and `billingContact` (per Q-B + Q-E). Required fields are
 * `name`/`email`/`phone`; `title` is optional. The pre-fill payload uses
 * `.partial()` to allow CRM-side hydration of any subset of these fields
 * (the customer fills in whatever the CRM doesn't already know).
 */
export const IntakePrimaryContactSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email(),
    phone: z.string().min(1).max(50),
    title: z.string().max(120).optional(),
});
/**
 * A single team's intake section. `individualCount` is the customer's
 * declared count (the form pre-populates from the Quote line item's
 * `teams[].individualCount`); `individuals[]` is the per-person grid.
 *
 * Per Q-C, `individuals.length` MUST equal `individualCount` at submit
 * time — prevents accidental partial intake. The form UI auto-syncs the
 * grid length to the declared count as the customer edits.
 */
export const TeamIntakeBaseSchema = z.object({
    teamName: z.string().min(1).max(120),
    // QA 2026-10-01 F-08 — the intake submit validates with this schema.
    individualCount: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX),
    individuals: z.array(IntakeIndividualSchema).max(EMPLOYEE_COUNT_MAX).default([]),
});
export const TeamIntakeSchema = TeamIntakeBaseSchema.superRefine((val, ctx) => {
    // Q-C — submit-time count must match the grid length. The form is
    // expected to keep the two in lockstep client-side; this refinement is
    // a server-side defense.
    if (val.individuals.length !== val.individualCount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['individuals'],
            message: `individuals.length (${val.individuals.length}) must equal individualCount (${val.individualCount})`,
        });
    }
});
/** Q-E.2 — required communication channel preference; no default. */
export const PreferredCommunicationChannelSchema = z.enum(['email', 'teams', 'slack', 'phone']);
/**
 * Form-submit payload. Sent to `POST /api/v1/intake/submit` (T6-007).
 *
 * Refinements layered on top of the object shape:
 *   - `dataHandlingConsent` MUST be `true` (the `z.literal(true)` does
 *     this at the type level; non-true values fail Zod parsing).
 *   - `engagementLead` is required when `engagementLeadSameAsPrimary` is
 *     `false` (Q-B).
 *   - `billingContact` is required when `billingContactSameAsPrimary` is
 *     `false` (Q-E.3).
 */
const IntakeFormBaseSchema = z.object({
    companyName: z.string().min(1).max(200),
    primaryContact: IntakePrimaryContactSchema,
    /** Q-B — default true; when true, engagementLead is omitted/ignored. */
    engagementLeadSameAsPrimary: z.boolean().default(true),
    /** Q-B — required by refinement when engagementLeadSameAsPrimary === false. */
    engagementLead: IntakePrimaryContactSchema.optional(),
    /** Q-E.3 — default true; when true, billingContact is omitted/ignored. */
    billingContactSameAsPrimary: z.boolean().default(true),
    /** Q-E.3 — required by refinement when billingContactSameAsPrimary === false. */
    billingContact: IntakePrimaryContactSchema.optional(),
    /** Q-E.5 — optional invoice / PO reference threaded into QBO invoicing. */
    poNumber: z.string().max(80).optional(),
    teams: z.array(TeamIntakeSchema).min(1).max(INTAKE_TEAMS_MAX),
    preferredKickoffDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'preferredKickoffDate must be YYYY-MM-DD')
        .optional(),
    /** Q-E.2 — required; no default. */
    preferredCommunicationChannel: PreferredCommunicationChannelSchema,
    /** Q-E.1 — required customer acknowledgment; literal `true`. */
    dataHandlingConsent: z.literal(true),
    additionalNotes: z.string().max(2000).optional(),
});
export const IntakeFormSchema = IntakeFormBaseSchema.superRefine((val, ctx) => {
    // Q-B — engagementLead required when not same-as-primary.
    if (val.engagementLeadSameAsPrimary === false && val.engagementLead === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['engagementLead'],
            message: 'engagementLead is required when engagementLeadSameAsPrimary is false',
        });
    }
    // Q-E.3 — billingContact required when not same-as-primary.
    if (val.billingContactSameAsPrimary === false && val.billingContact === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingContact'],
            message: 'billingContact is required when billingContactSameAsPrimary is false',
        });
    }
});
/**
 * Read-only pre-fill payload returned by `GET /api/v1/intake/validate?token=…`
 * (T6-003). The form merges these defaults into its initial state, then the
 * customer edits and submits.
 *
 * Per Q-E, the pre-fill MAY suggest a `preferredCommunicationChannel` value
 * if the CRM Account has a known preference; otherwise the field is omitted
 * and the form treats it as unselected. Pre-fill does NOT seed
 * `engagementLead`, `billingContact`, `poNumber`, `dataHandlingConsent` —
 * those are customer-only fields with no CRM-side defaults.
 */
export const IntakePrefillSchema = z.object({
    accountId: IDSchema,
    dealId: IDSchema,
    companyName: z.string(),
    primaryContact: IntakePrimaryContactSchema.partial().optional(),
    teams: z.array(z.object({
        teamName: z.string().optional(),
        individualCount: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX),
    })),
    /** Q-E.2 — optional suggestion sourced from CRM Account (when known). */
    preferredCommunicationChannel: PreferredCommunicationChannelSchema.optional(),
    tokenExpiresAt: IsoTimestampSchema,
    /**
     * 2026-11-04 trust review — set when this deal's intake has already been
     * submitted. The form then shows an "already submitted" screen instead of
     * an editable (and by then empty) form.
     */
    submittedAt: IsoTimestampSchema.optional(),
});
//# sourceMappingURL=intake.js.map